type EstimationFormData = {
  name: string;
  email: string;
  phone: string;
  budget: string;
  message: string;
  files: File[];
  website?: string; // Honeypot field
  recaptchaToken?: string; // reCAPTCHA token
};

type BookingFormData = {
  name: string;
  email: string;
  phone: string;
  company: string;
  projectType: string;
  message: string;
  date: string;
  time: string;
};

type CarbonReportFormData = {
  name: string;
  email: string;
  phone: string;
  file: File;
};

export const sendCarbonReportEmail = async (formData: CarbonReportFormData) => {
  const formDataToSend = new FormData();  
  
  // Add all form fields
  Object.entries(formData).forEach(([key, value]) => {
    if (key !== 'files' && typeof value === 'string') {
      formDataToSend.append(key, value);
    }
  });

  formDataToSend.append('files', formData.file, formData.file.name);

  try {
    const response = await fetch(`${import.meta.env.VITE_EMAIL_SERVER}/carbon-report`, {
      method: 'POST',
      body: formDataToSend,
    });

    if (!response.ok) {
      throw new Error('Failed to send carbon report email');
    }

    return response;
  } catch (error) {
    console.error('Error sending carbon report email:', error);
    throw error;
  }
};

export const sendEstimationEmail = async (formData: EstimationFormData) => {
  // Check honeypot field - if filled, it's likely a bot
  if (formData.website && formData.website.trim() !== '') {
    throw new Error('Spam detected');
  }

  const formDataToSend = new FormData();
  
  // Add all form fields except honeypot
  Object.entries(formData).forEach(([key, value]) => {
    if (key !== 'files' && key !== 'website' && typeof value === 'string') {
      formDataToSend.append(key, value);
    }
  });

  // Add reCAPTCHA token if available
  if (formData.recaptchaToken) {
    formDataToSend.append('recaptchaToken', formData.recaptchaToken);
  }

  // Add files if they exist
  if (formData.files && formData.files.length > 0) {
    formData.files.forEach((file, index) => {
      // Append each file directly to FormData
      formDataToSend.append('files', file, file.name);
    });
  }

  try {
    const response = await fetch(`${import.meta.env.VITE_EMAIL_SERVER}/estimation`, {
      method: 'POST',
      // Don't set Content-Type header - let the browser set it with the boundary
      body: formDataToSend,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || 'Failed to send estimation email');
    }

    return response;
  } catch (error) {
    console.error('Error sending estimation email:', error);
    throw error;
  }
};

export const sendBookingEmail = async (formData: BookingFormData) => {
  const response = await fetch(`${import.meta.env.VITE_EMAIL_SERVER}/booking`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    throw new Error('Failed to send booking email');
  }

  return response;
};